import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@gdec/shared';
import { api, configureAuth, errorMessage } from '@/api/client';

/**
 * Authentication state.
 *
 * The session is an HttpOnly cookie set by the API, so there is deliberately no
 * token in this module and nothing in localStorage: JavaScript cannot read the
 * credential, which is the point. "Am I signed in?" is answered by asking the
 * server (`session.me`), not by inspecting client state.
 */
/**
 * Which ways in the server currently offers. Answered by the API, not baked
 * into the bundle, so switching Microsoft sign-in on — or passwords off — is a
 * configuration change on the API rather than a front-end deploy.
 */
export interface SignInMethods {
  password: boolean;
  emailCode: boolean;
  microsoft: boolean;
}

export interface AuthState {
  user: User | null;
  /** True while the initial session restore or a sign-in is in flight. */
  loading: boolean;
  error: string | null;
  /** Null until the server has answered; the login page waits for it. */
  methods: SignInMethods | null;
  /** Sign in with a password. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Send a one-time code to a company address. */
  requestCode: (email: string) => Promise<void>;
  /** Complete sign-in with the emailed code. */
  signInWithCode: (email: string, code: string) => Promise<void>;
  /** Leave for Microsoft's sign-in page. Resolves only on failure to start. */
  signInWithMicrosoft: (returnTo?: string) => Promise<void>;
  /** Back from Microsoft: exchange the ticket in the URL for a session. */
  completeMicrosoft: (ticket: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-fetch `session.me`, e.g. after the profile-setup step. */
  refresh: () => Promise<void>;
  /**
   * Re-fetch the session only when the person just edited IS the signed-in one.
   *
   * An admin editing their own record through the Organization Chart changed
   * the database but not this cached user, so their department and employment
   * level stayed stale everywhere the session is read — the profile, the
   * sidebar, and the employee block printed on every new claim — until a full
   * page reload. Editing somebody else needs no refresh, so the id is checked
   * rather than reloading the session after every admin action.
   */
  refreshIfSelf: (userId: string) => Promise<void>;
  clearError: () => void;
  /**
   * The account is on an administrator-issued password and must replace it.
   *
   * Answered by the server on every session restore, not only at sign-in — see
   * the note in `refresh`.
   */
  mustChangePassword: boolean;
}

interface SessionInfo {
  user: User;
  unreadNotifications: number;
  mustChangePassword: boolean;
}

interface SignInResult {
  user: User;
  mustChangePassword: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  // Read through a ref so `refreshIfSelf` keeps a stable identity; it is called
  // from callbacks that would otherwise be rebuilt on every session change.
  const userRef = useRef<User | null>(null);
  userRef.current = user;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [methods, setMethods] = useState<SignInMethods | null>(null);

  // Asked once, unauthenticated. If the call fails the page falls back to
  // offering everything — a broken methods call must not lock everyone out.
  useEffect(() => {
    let alive = true;
    void api.auth
      .methods<SignInMethods>()
      .then((m) => {
        if (alive) setMethods(m);
      })
      .catch(() => {
        if (alive) setMethods({ password: true, emailCode: true, microsoft: false });
      });
    return () => {
      alive = false;
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const session = await api.session.me<SessionInfo>();
      setUser(session.user);
      // Re-read from the server rather than left alone, so the forced
      // change-password gate survives a page reload. Held only in memory, it
      // was cleared by F5 while the administrator-issued password stayed valid.
      setMustChangePassword(session.mustChangePassword);
    } catch {
      // A missing or expired cookie is the normal signed-out case, not an error
      // worth showing anybody.
      setUser(null);
    }
  }, []);

  // One call on mount decides whether the cookie we may or may not have is
  // still good.
  useEffect(() => {
    let alive = true;
    void (async () => {
      await refresh();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  // A 401/403 anywhere in the app means the session went away underneath us.
  useEffect(() => {
    configureAuth(() => setUser(null));
  }, []);

  const runSignIn = useCallback(async (call: () => Promise<SignInResult>): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await call();
      setUser(result.user);
      setMustChangePassword(result.mustChangePassword);
    } catch (err) {
      setError(errorMessage(err));
      setUser(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(
    (email: string, password: string) =>
      runSignIn(() => api.auth.signIn<SignInResult>({ email, password })),
    [runSignIn],
  );

  const signInWithCode = useCallback(
    (email: string, code: string) =>
      runSignIn(() => api.auth.verifyOtp<SignInResult>({ email, code })),
    [runSignIn],
  );

  const signInWithMicrosoft = useCallback(async (returnTo?: string): Promise<void> => {
    setError(null);
    try {
      const { url } = await api.auth.microsoftStart<{ url: string }>(returnTo ? { returnTo } : {});
      // A full navigation, on purpose: Microsoft's page must own the whole
      // window so the browser's own address bar shows who is asking.
      window.location.assign(url);
    } catch (err) {
      setError(errorMessage(err));
      throw err;
    }
  }, []);

  // Not through runSignIn: that flips the global `loading` flag, which swaps
  // the whole tree for a spinner and unmounts the callback page mid-flight —
  // and a remounted callback page finds the fragment already cleared and
  // reports the wrong error. The callback page shows its own progress.
  const completeMicrosoft = useCallback(async (ticket: string): Promise<void> => {
    setError(null);
    try {
      const result = await api.auth.microsoftComplete<SignInResult>({ ticket });
      setMustChangePassword(result.mustChangePassword);
      setUser(result.user);
    } catch (err) {
      setError(errorMessage(err));
      setUser(null);
      throw err;
    }
  }, []);

  const requestCode = useCallback(async (email: string): Promise<void> => {
    setError(null);
    try {
      await api.auth.requestOtp<{ sent: boolean }>({ email });
    } catch (err) {
      setError(errorMessage(err));
      throw err;
    }
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await api.auth.signOut<{ ok: boolean }>();
    } finally {
      // Clear local state even if the call failed — the user asked to leave,
      // and the cookie expires on its own regardless.
      setUser(null);
      setMustChangePassword(false);
    }
  }, []);

  const refreshIfSelf = useCallback(
    async (userId: string): Promise<void> => {
      if (userId && userId === userRef.current?.id) await refresh();
    },
    [refresh],
  );

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      error,
      methods,
      signIn,
      requestCode,
      signInWithCode,
      signInWithMicrosoft,
      completeMicrosoft,
      signOut,
      refresh,
      refreshIfSelf,
      clearError: () => setError(null),
      mustChangePassword,
    }),
    [
      user,
      loading,
      error,
      methods,
      signIn,
      requestCode,
      signInWithCode,
      signInWithMicrosoft,
      completeMicrosoft,
      signOut,
      refresh,
      refreshIfSelf,
      mustChangePassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>.');
  return ctx;
}

/** The signed-in user, for components rendered below the auth gate. */
export function useCurrentUser(): User {
  const { user } = useAuth();
  if (!user) throw new Error('useCurrentUser used outside an authenticated route.');
  return user;
}
